import React from "react";

/**
 * ScoreProgress Component
 * Visual progress bar with color coding and status
 *
 * @param {number} value - Percentage value (0-100)
 * @param {boolean} showStatus - Whether to show status label
 * @param {string} size - Size variant ("sm", "md", "lg")
 */
const ScoreProgress = ({ value = 0, showStatus = false, size = "md" }) => {
  // Ensure value is between 0 and 100
  const normalizedValue = Math.min(Math.max(value, 0), 100);

  // Determine color based on value
  const getColorClasses = () => {
    if (normalizedValue >= 70) {
      return {
        bg: "bg-green-500",
        text: "text-green-700",
        bgLight: "bg-green-100",
        status: "Excellent",
        statusBg: "bg-green-100",
        statusText: "text-green-800",
      };
    } else if (normalizedValue >= 60) {
      return {
        bg: "bg-yellow-500",
        text: "text-yellow-700",
        bgLight: "bg-yellow-100",
        status: "Good",
        statusBg: "bg-yellow-100",
        statusText: "text-yellow-800",
      };
    } else if (normalizedValue >= 50) {
      return {
        bg: "bg-orange-500",
        text: "text-orange-700",
        bgLight: "bg-orange-100",
        status: "Fair",
        statusBg: "bg-orange-100",
        statusText: "text-orange-800",
      };
    } else {
      return {
        bg: "bg-red-500",
        text: "text-red-700",
        bgLight: "bg-red-100",
        status: "Poor",
        statusBg: "bg-red-100",
        statusText: "text-red-800",
      };
    }
  };

  const colors = getColorClasses();

  // Size variants
  const sizeClasses = {
    sm: "h-1.5",
    md: "h-2",
    lg: "h-3",
  };

  const heightClass = sizeClasses[size] || sizeClasses.md;

  return (
    <div className="w-full">
      {/* Progress Bar Container */}
      <div className="flex items-center space-x-3">
        {/* Bar */}
        <div className="flex-1">
          <div className={`w-full ${colors.bgLight} rounded-full overflow-hidden ${heightClass}`}>
            <div
              className={`${colors.bg} ${heightClass} rounded-full transition-all duration-500 ease-out shadow-sm`}
              style={{ width: `${normalizedValue}%` }}
              role="progressbar"
              aria-valuenow={normalizedValue}
              aria-valuemin="0"
              aria-valuemax="100"
              aria-label={`Score progress: ${normalizedValue}%`}
            />
          </div>
        </div>

        {/* Percentage Text */}
        <div className={`text-sm font-semibold ${colors.text} min-w-[3rem] text-right`}>
          {normalizedValue.toFixed(1)}%
        </div>
      </div>

      {/* Status Label */}
      {showStatus && (
        <div className="mt-2 flex items-center justify-between">
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors.statusBg} ${colors.statusText}`}
          >
            {colors.status}
          </span>
          <span className="text-xs text-gray-500">
            {normalizedValue >= 70
              ? "Meeting requirements"
              : normalizedValue >= 50
              ? "Needs improvement"
              : "Below standards"}
          </span>
        </div>
      )}
    </div>
  );
};

export default ScoreProgress;